import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { useToast } from '@/components/common/Toast';
import { supabase } from '@/lib/supabase';

// 설정 관련 모달 컴포넌트들
import { PhoneChangeModal } from '@/components/settings/phone/PhoneChangeModal';
import { EmailChangeModal } from '@/components/settings/email/EmailChangeModal';
import { PasswordChangeModal } from '@/components/settings/password/PasswordChangeModal';
import { AccountDeletionModal } from '@/components/settings/account/AccountDeletionModal';

interface SellerData {
  id: string;
  email: string;
  phone: string;
  name: string;
  role: string;
  business_name?: string;
  business_registration_number?: string;
}

export function DesktopSellerSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();
  
  const [sellerData, setSellerData] = useState<SellerData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // 모달 상태들
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAccountDeletionModal, setShowAccountDeletionModal] = useState(false);

  // 판매자 데이터 가져오기
  useEffect(() => {
    const fetchSellerData = async () => {
      if (!user?.id || user.role !== 'seller') return;

      try {
        // users와 sellers 테이블 조인하여 데이터 가져오기
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, email, phone, name, role')
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('Failed to fetch user data:', userError);
          showToast('사용자 정보를 불러오는데 실패했습니다');
          return;
        }

        // businesses 테이블에서 추가 정보 가져오기
        const { data: sellerInfo, error: sellerError } = await supabase
          .from('businesses')
          .select('business_name, business_number')
          .eq('owner_id', user.id)
          .single();

        if (sellerError) {
          console.error('Failed to fetch seller info:', sellerError);
          // 판매자 정보가 없어도 기본 사용자 정보는 표시
        }

        setSellerData({
          ...userData,
          business_name: sellerInfo?.business_name,
          business_registration_number: sellerInfo?.business_number
        });
      } catch (error) {
        console.error('Error fetching seller data:', error);
        showToast('사용자 정보를 불러오는데 실패했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSellerData();
  }, [user, showToast]);

  const handleRefreshData = async () => {
    if (!user?.id) return;
    setIsLoading(true);
    
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, phone, name, role')
        .eq('id', user.id)
        .single();

      if (userError) {
        showToast('데이터 새로고침에 실패했습니다');
        return;
      }

      const { data: sellerInfo } = await supabase
        .from('sellers')
        .select('business_name, business_registration_number')
        .eq('user_id', user.id)
        .single();

      setSellerData({
        ...userData,
        business_name: sellerInfo?.business_name,
        business_registration_number: sellerInfo?.business_registration_number
      });
      showToast('정보가 업데이트되었습니다');
    } catch (error) {
      showToast('데이터 새로고침에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <ProtectedRoute requiredRole="seller" minimalLoading={true}>
        <DesktopSellerPageContainer>
          <div className="flex items-center justify-center min-h-[600px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
              <p className="text-[18px] text-gray-600">설정 정보를 불러오는 중...</p>
            </div>
          </div>
        </DesktopSellerPageContainer>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requiredRole="seller" minimalLoading={true}>
      <DesktopSellerPageContainer>
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-800 mr-4"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-[32px] font-bold text-gray-800">설정</h1>
          </div>

          {sellerData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Account Settings */}
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <h2 className="text-[24px] font-semibold text-gray-800 mb-6">계정 정보</h2>
                  
                  {/* 이메일 설정 */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="block text-[16px] font-medium text-gray-700 mb-2">
                          이메일
                        </label>
                        <p className="text-[18px] text-gray-900 truncate">
                          {sellerData.email}
                        </p>
                      </div>
                      <button
                        onClick={() => setShowEmailModal(true)}
                        className="ml-4 px-6 py-3 bg-green-500 text-white text-[16px] font-medium rounded-lg hover:bg-green-600 transition-colors"
                      >
                        변경
                      </button>
                    </div>
                  </div>

                  {/* 비밀번호 설정 */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="block text-[16px] font-medium text-gray-700 mb-2">
                          비밀번호
                        </label>
                        <p className="text-[18px] text-gray-600">
                          ••••••••
                        </p>
                      </div>
                      <button
                        onClick={() => setShowPasswordModal(true)}
                        className="ml-4 px-6 py-3 bg-green-500 text-white text-[16px] font-medium rounded-lg hover:bg-green-600 transition-colors"
                      >
                        변경
                      </button>
                    </div>
                  </div>

                  {/* 전화번호 설정 */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="block text-[16px] font-medium text-gray-700 mb-2">
                          전화번호
                        </label>
                        <p className="text-[18px] text-gray-900">
                          {sellerData.phone || '등록되지 않음'}
                        </p>
                      </div>
                      <button
                        onClick={() => setShowPhoneModal(true)}
                        className="ml-4 px-6 py-3 bg-green-500 text-white text-[16px] font-medium rounded-lg hover:bg-green-600 transition-colors"
                      >
                        변경
                      </button>
                    </div>
                  </div>
                </div>

                {/* 계정 관리 */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <h3 className="text-[20px] font-semibold text-gray-800 mb-4">계정 관리</h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <h4 className="text-[16px] font-medium text-orange-800 mb-2">계정 탈퇴</h4>
                      <p className="text-[14px] text-orange-700 mb-3">
                        계정을 탈퇴하면 모든 데이터가 삭제되며 복구할 수 없습니다.
                      </p>
                      <button
                        onClick={() => setShowAccountDeletionModal(true)}
                        className="text-[16px] text-red-600 hover:text-red-800 transition-colors underline font-medium"
                      >
                        탈퇴하기
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Business Information */}
              <div className="space-y-6">
                {/* 사업자 정보 (판매자 전용) */}
                {sellerData.business_name && (
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <h3 className="text-[24px] font-semibold text-gray-800 mb-6">사업자 정보</h3>
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <label className="block text-[16px] font-medium text-blue-700 mb-2">
                          상호명
                        </label>
                        <p className="text-[18px] text-blue-900">
                          {sellerData.business_name}
                        </p>
                      </div>
                      {sellerData.business_registration_number && (
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <label className="block text-[16px] font-medium text-blue-700 mb-2">
                            사업자등록번호
                          </label>
                          <p className="text-[18px] text-blue-900 font-mono">
                            {sellerData.business_registration_number}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-[16px] text-yellow-800">
                        💡 사업자 정보 변경은 고객센터로 문의해주세요
                      </p>
                      <p className="text-[14px] text-yellow-700 mt-1">
                        이메일: ecodeal_cs@ecodeal.kr
                      </p>
                    </div>
                  </div>
                )}

                {/* 보안 및 개인정보 */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <h3 className="text-[20px] font-semibold text-gray-800 mb-4">보안 및 개인정보</h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="text-[16px] font-medium text-green-800 mb-2">✅ 보안 설정</h4>
                      <ul className="text-[14px] text-green-700 space-y-1">
                        <li>• 이메일 인증 완료</li>
                        <li>• 강력한 비밀번호 사용 권장</li>
                        <li>• 정기적인 비밀번호 변경 권장</li>
                      </ul>
                    </div>
                    
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="text-[16px] font-medium text-gray-700 mb-2">📋 개인정보 처리</h4>
                      <p className="text-[14px] text-gray-600">
                        회원님의 개인정보는 관련 법령에 따라 안전하게 보호되며, 
                        서비스 제공 목적으로만 사용됩니다.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 고객지원 */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <h3 className="text-[20px] font-semibold text-gray-800 mb-4">고객지원</h3>
                  <div className="space-y-3">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-[16px] font-medium text-gray-700 mb-2">📧 이메일 문의</h4>
                      <p className="text-[16px] text-blue-600">ecodeal_cs@ecodeal.kr</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-[16px] font-medium text-gray-700 mb-2">🕒 운영시간</h4>
                      <p className="text-[14px] text-gray-600">평일 09:00 - 18:00 (주말, 공휴일 제외)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 모달들 */}
          <PhoneChangeModal
            isOpen={showPhoneModal}
            onClose={() => setShowPhoneModal(false)}
            currentPhone={sellerData?.phone || ''}
            userId={sellerData?.id || ''}
            onSuccess={() => {
              handleRefreshData();
              showToast('전화번호가 변경되었습니다');
            }}
          />
          
          <EmailChangeModal
            isOpen={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            currentEmail={sellerData?.email || ''}
            userId={sellerData?.id || ''}
            userName={sellerData?.name || ''}
            onSuccess={() => {
              handleRefreshData();
              showToast('이메일이 변경되었습니다');
            }}
          />
          
          <PasswordChangeModal
            isOpen={showPasswordModal}
            onClose={() => setShowPasswordModal(false)}
            userEmail={sellerData?.email || ''}
            userName={sellerData?.name || ''}
            onSuccess={() => {
              setShowPasswordModal(false);
              showToast('비밀번호가 변경되었습니다');
            }}
          />
          
          <AccountDeletionModal
            isOpen={showAccountDeletionModal}
            onClose={() => setShowAccountDeletionModal(false)}
            userRole="seller"
            userData={sellerData}
          />

          <ToastContainer />
        </div>
      </DesktopSellerPageContainer>
    </ProtectedRoute>
  );
}